import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowLeft, Send, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [commentForm, setCommentForm] = useState({
    author_name: '',
    author_email: '',
    content: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/blog/posts/${slug}`, {
        params: { increment_view: true }
      });
      setPost(response.data);
      fetchComments(response.data.id);
    } catch (error) {
      console.error('Failed to fetch post:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (postId) => {
    try {
      const response = await axios.get(`${API_URL}/api/comments/${postId}`);
      setComments(response.data.comments);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const handleCommentChange = (e) => {
    const { name, value } = e.target;
    setCommentForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/comments`, {
        ...commentForm,
        post_id: post.id
      });
      toast.success('Comment submitted for review. It will appear after approval.');
      setCommentForm({ author_name: '', author_email: '', content: '' });
    } catch (error) {
      toast.error('Failed to submit comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-32 pb-20">
          <div className="container-custom max-w-4xl">
            <div className="animate-pulse">
              <div className="h-8 bg-slate-200 rounded w-1/4 mb-6"></div>
              <div className="h-12 bg-slate-200 rounded mb-4"></div>
              <div className="h-4 bg-slate-200 rounded w-1/3 mb-8"></div>
              <div className="h-64 bg-slate-200 rounded mb-8"></div>
              <div className="space-y-4">
                <div className="h-4 bg-slate-200 rounded"></div>
                <div className="h-4 bg-slate-200 rounded"></div>
                <div className="h-4 bg-slate-200 rounded w-3/4"></div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-32 pb-20">
          <div className="container-custom max-w-4xl text-center">
            <h1 className="font-heading text-3xl font-bold text-[#2D3E55] mb-4">
              Post Not Found
            </h1>
            <p className="font-body text-slate-600 mb-6">
              The blog post you're looking for doesn't exist.
            </p>
            <Link to="/blog">
              <Button className="bg-[#2D3E55]">
                <ArrowLeft size={18} className="mr-2" />
                Back to Blog
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <article className="pt-32 pb-20" data-testid="blog-post-article">
        <div className="container-custom max-w-4xl">
          {/* Back Link */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-8"
          >
            <Link 
              to="/blog"
              className="inline-flex items-center gap-2 text-slate-600 hover:text-[#F15A24] transition-colors font-body"
              data-testid="back-to-blog"
            >
              <ArrowLeft size={18} />
              Back to Blog
            </Link>
          </motion.div>

          {/* Post Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Badge variant="secondary" className="bg-[#F15A24]/10 text-[#F15A24] mb-4">
              {post.category}
            </Badge>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#2D3E55] mb-6" data-testid="post-title">
              {post.title}
            </h1>
            <div className="flex items-center gap-4 text-slate-500 font-body text-sm">
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                <span>{formatDate(post.created_at)}</span>
              </div>
              <span>•</span>
              <span>{post.view_count} views</span>
            </div>
          </motion.header>

          {/* Featured Image */}
          {post.featured_image && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-10"
            >
              <img 
                src={post.featured_image}
                alt={post.title}
                className="w-full h-auto rounded-2xl shadow-lg"
                data-testid="post-featured-image"
              />
            </motion.div>
          )}

          {/* Post Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="blog-content prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
            data-testid="post-content"
          />

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mt-10 pt-6 border-t"
            >
              <div className="flex flex-wrap gap-2">
                {post.tags.map(tag => (
                  <Badge key={tag} variant="outline" className="text-[#2D3E55]">
                    #{tag}
                  </Badge>
                ))}
              </div>
            </motion.div>
          )}

          <Separator className="my-12" />

          {/* Comments Section */}
          <section data-testid="comments-section">
            <h2 className="font-heading text-2xl font-semibold text-[#2D3E55] mb-8">
              Comments ({comments.length})
            </h2>

            {/* Existing Comments */}
            {comments.length > 0 ? (
              <div className="space-y-6 mb-12">
                {comments.map((comment) => (
                  <Card key={comment.id} data-testid={`comment-${comment.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#2D3E55]/10 flex items-center justify-center flex-shrink-0">
                          <User size={20} className="text-[#2D3E55]" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-body font-semibold text-[#2D3E55]">
                              {comment.author_name}
                            </span>
                            <span className="text-slate-400 text-sm">•</span>
                            <span className="font-body text-sm text-slate-500">
                              {formatDate(comment.created_at)}
                            </span>
                          </div>
                          <p className="font-body text-slate-600">
                            {comment.content}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <p className="font-body text-slate-600 mb-8">
                No comments yet. Be the first to share your thoughts!
              </p>
            )}

            {/* Comment Form */}
            <Card data-testid="comment-form">
              <CardContent className="p-6">
                <h3 className="font-heading text-xl font-semibold text-[#2D3E55] mb-6">
                  Leave a Comment
                </h3>
                <form onSubmit={handleCommentSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="author_name">Your Name *</Label>
                      <Input
                        id="author_name"
                        name="author_name"
                        value={commentForm.author_name}
                        onChange={handleCommentChange}
                        required
                        className="mt-2"
                        data-testid="comment-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="author_email">Your Email *</Label>
                      <Input
                        id="author_email"
                        name="author_email"
                        type="email"
                        value={commentForm.author_email}
                        onChange={handleCommentChange}
                        required
                        className="mt-2"
                        data-testid="comment-email-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="content">Your Comment *</Label>
                    <Textarea
                      id="content"
                      name="content"
                      value={commentForm.content}
                      onChange={handleCommentChange}
                      required
                      rows={4}
                      className="mt-2"
                      placeholder="Share your thoughts..."
                      data-testid="comment-content-input"
                    />
                  </div>
                  <p className="font-body text-sm text-slate-500">
                    Your comment will be reviewed before it appears publicly.
                  </p>
                  <Button 
                    type="submit"
                    disabled={submitting}
                    className="bg-[#F15A24] hover:bg-[#d64d1e] text-white"
                    data-testid="submit-comment-btn"
                  >
                    {submitting ? 'Submitting...' : (
                      <>
                        <Send size={16} className="mr-2" />
                        Submit Comment
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </section>
        </div>
      </article>

      <Footer />
    </div>
  );
}
